/* eslint-disable no-use-before-define */
import Amplify, { Auth } from 'aws-amplify';
import { views } from '@/router/constants';
import store from '@/store';
import i18n from '@/i18n';
import {
  SET_AUTH,
  CLEAR_AUTH,
  SET_CHALLENGE,
  FIND_USER_POOL_ID,
  FIND_USER_POOL_CLIENT_ID,
  CLEAR_PREFILL_DATA,
} from '@/store/auth/actionTypes';

import config from './config';

import { authErrors, AuthError } from './errors';
import { authChallenges } from './constants';

export function setConfig() {
  Amplify.configure(config);
}

export async function getUser(bypassCache) {
  try {
    const user = await Auth.currentAuthenticatedUser({ bypassCache });
    return user;
  } catch (e) {
    return null;
  }
}

export async function getFileUrl(item) {
  try {
    const extraArgs = {
      level: 'protected',
      identityId: localStorage.getItem('identityId'),
      expires: 3600,
    };
    const itemUrl = await Amplify.Storage.get(item, extraArgs);
    return itemUrl;
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function getAuth(bypassCache = false) {
  const user = await self.getUser(bypassCache);
  return user && user.signInUserSession.idToken.payload;
}

export async function signIn(email, password, domain) {
  if (store.getters.isServiceProvider.value === true) {
    localStorage.setItem('userPoolId', process.env.VUE_APP_AMPLIFY_COGNITO_USER_POOL_ID);
    localStorage.setItem('webClientId', process.env.VUE_APP_AMPLIFY_COGNITO_APP_CLIENT_ID);
  } else {
    try {
      await store.dispatch(FIND_USER_POOL_ID, { domain });
      await store.dispatch(FIND_USER_POOL_CLIENT_ID);
    } catch (err) {
      console.error(err);
    }
    localStorage.setItem('userPoolId', config.Auth.userPoolId);
    localStorage.setItem('identityId', config.Auth.identityPoolId);
    localStorage.setItem('bucketName', config.Auth.userPoolId.replace(/_/g, '').toLowerCase());
    localStorage.setItem('webClientId', config.Auth.userPoolWebClientId);
  }
  setConfig();
  const user = await Auth.signIn(email, password);
  return self.handleSignedInUser(user);
}

export async function signOut() {
  await Auth.signOut();
  localStorage.removeItem('userPoolId');
  localStorage.removeItem('webClientId');
  localStorage.removeItem('bucketName');
  localStorage.removeItem('domain');
  localStorage.removeItem('identityId');
  localStorage.clear();
  await store.dispatch(CLEAR_AUTH);
  await store.dispatch(CLEAR_PREFILL_DATA);
}

export async function challengeChangePassword(newPassword) {
  const { challengeUser, challengeName } = self.getStoreAuthChallenge();
  if (challengeName !== authChallenges.NEW_PASSWORD_REQUIRED || !challengeUser) {
    throw new AuthError(authErrors.NOT_AUTHORIZED);
  }
  const { requiredAttributes } = await challengeUser.challengeParam.requiredAttributes; // the array of required attributes, e.g ['email', 'phone_number']
  let user;
  try {
    user = await Auth.completeNewPassword(challengeUser, newPassword, requiredAttributes);
  } catch (e) {
    console.log(e);
  }

  return self.handleSignedInUser(user);
}

export async function challengeSetupMFA() {
  const { challengeUser, challengeName } = self.getStoreAuthChallenge();
  if (challengeName !== authChallenges.MFA_SETUP || !challengeUser) {
    throw new AuthError(authErrors.NOT_AUTHORIZED);
  }

  const code = await Auth.setupTOTP(challengeUser);
  const issuer = i18n.t('app.title');
  const str = `otpauth://totp/${challengeUser.username}?secret=${code}&issuer=${issuer}`;
  return str;
}

export async function challengeVerifySetupMFACode(code) {
  const { challengeUser, challengeName } = self.getStoreAuthChallenge();
  if (challengeName !== authChallenges.MFA_SETUP || !challengeUser) {
    throw new AuthError(authErrors.NOT_AUTHORIZED);
  }

  const userSession = await Auth.verifyTotpToken(challengeUser, code);
  return self.handleSignedInUserSession(userSession);
}

export async function challengeVerifyMFACode(code) {
  const { challengeUser, challengeName } = self.getStoreAuthChallenge();
  if (challengeName !== authChallenges.SOFTWARE_TOKEN_MFA || !challengeUser) {
    throw new AuthError(authErrors.NOT_AUTHORIZED);
  }

  const user = await Auth.confirmSignIn(challengeUser, code, 'SOFTWARE_TOKEN_MFA');
  return self.handleSignedInUser(user);
}

async function init() {
  try {
    const auth = await self.getAuth();

    if (auth) {
      await store.dispatch(SET_AUTH, auth);
    } else {
      await store.dispatch(CLEAR_AUTH);
    }
  } catch (e) {
    await store.dispatch(CLEAR_AUTH);
  }
}

async function handleSignedInUserSession(userSession) {
  if (userSession && userSession.idToken) {
    const idTokenPayload = userSession.idToken.payload;
    await store.dispatch(SET_AUTH, idTokenPayload);
    return idTokenPayload;
  }

  throw new AuthError(authErrors.GENERAL);
}

async function handleSignedInUser(user) {
  if (user && user.signInUserSession && user.signInUserSession.idToken) {
    return self.handleSignedInUserSession(user.signInUserSession);
  }

  const { challengeName } = user || {};
  if (challengeName) {
    await store.dispatch(SET_CHALLENGE, { challengeName, challengeUser: user });
    throw new AuthError(authErrors.CHALLENGE_REQUIRED);
  }

  throw new AuthError(authErrors.GENERAL);
}

export function getSignInNextStepRoute() {
  const { challengeName } = self.getStoreAuthChallenge();
  if (challengeName) {
    switch (challengeName) {
    case authChallenges.NEW_PASSWORD_REQUIRED:
      return views.SIGN_IN_CHANGE_PASSWORD;
    case authChallenges.MFA_SETUP:
      return views.SIGN_IN_MFA_SETUP;
    case authChallenges.SOFTWARE_TOKEN_MFA:
      return views.SIGN_IN_MFA_CODE;
    default:
      return null;
    }
  } else if (!store.getters.auth) {
    return views.MODERN_SIGN_IN;
  }

  return null;
}

function getStoreAuthChallenge() {
  return store.getters.authChallenge;
}

// Unit-tests purposes only
export const self = {
  getUser,
  getAuth,
  handleSignedInUserSession,
  handleSignedInUser,
  getStoreAuthChallenge,
  init,
};

init();
